import React, { useEffect, useState, useRef, useCallback, useReducer } from "react";
import { Container, Title, TextInput, Textarea, Text, LoadingOverlay, Alert, Button, Group, Grid, Space, NativeSelect, ActionIcon, FileButton, Image, Select } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconTrash, IconDownload } from "@tabler/icons";
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useMatch, useNavigate } from "@tanstack/react-location";
import autoAnimate from "@formkit/auto-animate";
import { CountrySelect, DeadlineRow, DateModal, TrademarkPdf } from "./../components";
import * as App from "./../model/App";

export default function TrademarkScreen() {
    const { params: { id } } = useMatch();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { isLoading, isError, data } = useQuery([`trademark_${id}`], async () => { 
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        return App.Firebase.Read.getTrademarkById(userID, id); 
    });
    
    // For animating the deadline list...
    const refDeadlineList = useRef(null);       
    useEffect(() => {
        refDeadlineList.current && autoAnimate(refDeadlineList.current, { duration: 100 });
    }, [refDeadlineList]);

    // Load clients...
    const { isLoading: isLoadingClients, isError: isErrorClients, data: clients } = useQuery(["clients"], async () => { 
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        return App.Firebase.Read.getClients(userID); 
    });

    // Load demographics...
    const { isLoading: isLoadingDemographics, isError: isErrorDemographics, data: userDemographics } = useQuery(["userDemographics"], async () => { 
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        return App.Firebase.Read.getUserDemographics(userID + ""); 
    });

    const [, forceUpdate] = useReducer(x => x + 1, 0);
    const [isLoadingManual, setIsLoadingManual] = useState<boolean>(false);
    const [isShowingOfficeActionModal, setIsShowingOfficeActionModal] = useState<boolean>(false);
    const [clientId, setClientId] = useState<string | undefined>(undefined);
    const [referenceNumber, setReferenceNumber] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [imageUrl, setImageUrl] = useState<string>("");
    const [internationalRegistration, setInternationalRegistration] = useState<string>("");
    const [applicationNumber, setApplicationNumber] = useState<string>("");
    const [filingDate, setFilingDate] = useState<Date | null>(null);
    const [prioritySerialNumber, setPrioritySerialNumber] = useState<string>("");
    const [priorityDate, setPriorityDate] = useState<Date | null>(null);
    const [countryOfPriority, setCountryOfPriority] = useState<string>("");
    const [classNumber, setClassNumber] = useState<string>("");
    const [descriptionOfGoods, setDescriptionOfGoods] = useState<string>("");
    const [registrationNumber, setRegistrationNumber] = useState<string>("");
    const [registrationDate, setRegistrationDate] = useState<Date | null>(null);
    const [expirationDate, setExpirationDate] = useState<Date | null>(null);
    const [owners, setOwners] = useState<string[]>([]);
    const [attorneyOfRecord, setAttorneyOfRecord] = useState<string>("");
    const [lastRenewalDate, setLastRenewalDate] = useState<Date | null>(null);
    const [deadlines, setDeadlines] = useState<App.Data.App.IDeadline[]>([]);

    useEffect(() => {
        if (data) {
            setClientId(data.clientId);
            setReferenceNumber(data.referenceNumber);
            setTitle(data.title);
            setImageUrl(data.imageUrl);
            setInternationalRegistration(data.internationalRegistration);
            setApplicationNumber(data.applicationNumber);
            setFilingDate(data.filingDate ? new Date(data.filingDate) : null);
            setPrioritySerialNumber(data.prioritySerialNumber);
            setPriorityDate(data.priorityDate ? new Date(data.priorityDate) : null);
            setCountryOfPriority(data.countryOfPriority);
            setClassNumber(data.classNumber);
            setDescriptionOfGoods(data.descriptionOfGoods);
            setRegistrationNumber(data.registrationNumber);
            setRegistrationDate(data.registrationDate ? new Date(data.registrationDate) : null);
            setExpirationDate(data.expirationDate ? new Date(data.expirationDate) : null);
            setOwners(data.owners ? data.owners : []);
            setAttorneyOfRecord(data.attorneyOfRecord);
            setLastRenewalDate(data.lastRenewalDate ? new Date(data.lastRenewalDate) : null);
            setDeadlines(data.deadlines ? data.deadlines : []);
        }
    }, [data]);

    const getTrademarkFromState = useCallback((): App.Data.App.ITrademark => {
        return { 
            clientId: clientId || "",
            referenceNumber: referenceNumber || "",
            title: title, 
            imageUrl: imageUrl,
            internationalRegistration: internationalRegistration,
            applicationNumber: applicationNumber,
            filingDate: (filingDate ? filingDate.getTime() : null), 
            prioritySerialNumber: prioritySerialNumber,
            priorityDate: (priorityDate ? priorityDate.getTime() : null),
            countryOfPriority: countryOfPriority,
            classNumber: classNumber,
            descriptionOfGoods: descriptionOfGoods,
            registrationNumber: registrationNumber, 
            registrationDate: (registrationDate ? registrationDate.getTime() : null),
            expirationDate: (expirationDate ? expirationDate.getTime() : null),
            owners: owners,
            attorneyOfRecord: attorneyOfRecord,
            lastRenewalDate: (lastRenewalDate ? lastRenewalDate.getTime() : null),
            deadlines: deadlines
        }
    }, [clientId, referenceNumber, title, imageUrl, internationalRegistration, applicationNumber, filingDate, 
        prioritySerialNumber, priorityDate, countryOfPriority, classNumber, descriptionOfGoods, registrationNumber,
        registrationDate, expirationDate, owners, attorneyOfRecord, lastRenewalDate, deadlines]);

    const saveTrademark = useMutation(async () => { 
        const trademark: App.Data.App.ITrademark = getTrademarkFromState();
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        return App.Firebase.Write.updateTrademark(userID, id, trademark); 
        }, { onSuccess: () => { queryClient.invalidateQueries([`trademark_${id}`]); } }
    );

    // Occurs when image is uploaded...
    const onUploadImage = useCallback(async (payload: File) => {
        setIsLoadingManual(true);
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        App.Firebase.Write.uploadImage(userID, id, payload).then((url: string | null) => {
            setIsLoadingManual(false);
            if (url) { setImageUrl(url); }
        });
    }, [id, setIsLoadingManual, setImageUrl]);

    // Occurs when "Delete Image" is clicked...
    const onDeleteImageClick = useCallback(async () => {
        setIsLoadingManual(true);
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        App.Firebase.Write.deleteImage(userID, id).then(() => {
            setImageUrl("");
            setIsLoadingManual(false);
        });       
    } , [id, setIsLoadingManual, setImageUrl]);

    return (
        <Container ml={0}>
            <DateModal visible={isShowingOfficeActionModal} onClose={() => { setIsShowingOfficeActionModal(false); }} onSave={(date: Date) => {
                setIsShowingOfficeActionModal(false);

                const typesToDueDates: { type: string, dueDate: Date }[] = [];
                typesToDueDates.push({ type: "OFFICE ACTION RESPONSE", dueDate: App.Data.App.DateUtils.addMonthsToDate(date, 2) });
                typesToDueDates.push({ type: "EXTENSION OFFICE ACTION RESPONSE", dueDate: App.Data.App.DateUtils.addMonthsToDate(date, 4) });

                // Update deadlines as needed...
                const newDeadlines = deadlines;
                typesToDueDates.forEach((typeToDueDate) => {
                    const deadline = newDeadlines.findIndex(x => x.title === typeToDueDate.type);
                    if (deadline !== -1) {
                        newDeadlines[deadline].dueDate = typeToDueDate.dueDate.getTime();
                    } else {
                        newDeadlines.push({ title: typeToDueDate.type, description: "", dueDate: typeToDueDate.dueDate.getTime(), isComplete: false });
                    }            
                });
        
                setDeadlines(newDeadlines);
                forceUpdate();
            }} />  

            <Group position="apart">
                <Title>Trademark Details</Title>
                <div>
                    <PDFDownloadLink document={<TrademarkPdf trademark={data} clients={clients} />} fileName="trademark.pdf">
                        <Button leftIcon={<IconDownload size={14} />} variant="subtle">Download pdf</Button>
                    </PDFDownloadLink>
                    <Button onClick={() => { saveTrademark.mutate(); navigate({ to: "/trademarks" }); }}>Save</Button>
                </div>                    
            </Group>

            <LoadingOverlay visible={isLoading || isLoadingClients || saveTrademark.isLoading || isLoadingManual} />
            {(isError || isErrorClients) && <Alert color="red" title="Error">Error loading data</Alert>}
            {saveTrademark.isError && <Alert color="red" title="Error">Error saving trademark</Alert>}

            {data &&
                <>
                <Grid>
                    <Grid.Col span={8}>
                        <TextInput label="Title" value={title} onChange={(event) => { setTitle(event.target.value); }} />
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <TextInput label="Reference number" value={referenceNumber} onChange={(event) => { setReferenceNumber(event.target.value); }} />
                    </Grid.Col>

                    {imageUrl && 
                    <Grid.Col span={12}>
                        <Image width={200} radius="md" fit="contain" alt="Trademark" src={imageUrl} />
                    </Grid.Col>
                    }

                    <Grid.Col span={12}>
                        <FileButton onChange={onUploadImage}>{(props) => <Button {...props}>Upload Image</Button>}</FileButton>
                        {imageUrl && <Button ml={10} onClick={onDeleteImageClick}>Delete Image</Button>}
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <TextInput label="International registration" value={internationalRegistration} onChange={(event) => { setInternationalRegistration(event.target.value); }} />
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <TextInput label="Application number" value={applicationNumber} onChange={(event) => { setApplicationNumber(event.target.value); }} />
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <DatePicker label="Filing date" value={filingDate ? new Date(filingDate) : null} onChange={(date: Date) => {
                            setFilingDate(date);
                            if (!date) { return; }

                            const tm = { ...getTrademarkFromState(), filingDate: date.getTime() };
                            const deadlines = App.Data.App.TrademarkUtils.validateDeadlines(tm);
                            const renewalDue = deadlines.find(x => x.title === "RENEWAL DUE");
                            if (renewalDue && renewalDue.dueDate) { setExpirationDate(new Date(renewalDue.dueDate)); }
                            setDeadlines(deadlines);
                            forceUpdate();
                        }} />
                    </Grid.Col>

                    <Grid.Col span={6}>
                        <TextInput label="Priority serial number" value={prioritySerialNumber} onChange={(event) => { setPrioritySerialNumber(event.target.value); }} />
                    </Grid.Col>

                    <Grid.Col span={6}>
                        <DatePicker label="Priority date" value={priorityDate ? new Date(priorityDate) : null} onChange={setPriorityDate} />
                    </Grid.Col>

                    <Grid.Col span={6}>
                        <CountrySelect value={countryOfPriority ?? ""} label="Country of priority" placeholder="Country" onChange={(value) => { setCountryOfPriority(value); }} />
                    </Grid.Col>

                    <Grid.Col span={6}>
                        <TextInput label="Class number" value={classNumber} onChange={(event) => { setClassNumber(event.target.value); }} />
                    </Grid.Col>

                    <Grid.Col span={12}>
                        <Textarea rows={5} label="Description of goods or services" value={descriptionOfGoods} onChange={(event) => { setDescriptionOfGoods(event.target.value); }} />
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <TextInput label="Registration number" value={registrationNumber} onChange={(event) => { setRegistrationNumber(event.target.value); }} />
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <DatePicker label="Registration date" value={registrationDate ? new Date(registrationDate) : null} onChange={(date: Date) => {
                            setRegistrationDate(date);
                            if (!date) { return; }
                            
                            const tm = { ...getTrademarkFromState(), registrationDate: date.getTime() };
                            const deadlines = App.Data.App.TrademarkUtils.validateDeadlines(tm);
                            const renewalDue = deadlines.find(x => x.title === "RENEWAL DUE");
                            if (renewalDue && renewalDue.dueDate) { setExpirationDate(new Date(renewalDue.dueDate)); }
                            setDeadlines(deadlines);
                            forceUpdate();
                        }} />
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <DatePicker label="Expiration date" value={expirationDate ? new Date(expirationDate) : null} onChange={setExpirationDate} />
                    </Grid.Col>

                    <Grid.Col span={8}>
                        <Select
                            label="Attorney of record" placeholder="Attorney of record"
                            searchable
                            value={attorneyOfRecord}
                            data={userDemographics?.attorneys || []}
                            onChange={(value: string | null) => { setAttorneyOfRecord(value || ""); } }
                        />
                    </Grid.Col>

                    <Grid.Col span={4}>
                        <DatePicker label="Last renewal date" value={lastRenewalDate ? new Date(lastRenewalDate) : null} onChange={(date: Date) => {
                            setLastRenewalDate(date);
                            const lastRenewalDate = (date) ? date.getTime() : null;

                            const tm = { ...getTrademarkFromState(), lastRenewalDate: lastRenewalDate };
                            const deadlines = App.Data.App.TrademarkUtils.validateDeadlines(tm);
                            const renewalDue = deadlines.find(x => x.title === "RENEWAL DUE");
                            if (renewalDue && renewalDue.dueDate) { setExpirationDate(new Date(renewalDue.dueDate)); }
                            setDeadlines(deadlines);
                            forceUpdate();
                        }} />
                    </Grid.Col>

                </Grid>

                <NativeSelect my={10} label="Client" value={clients && clientId && clients[clientId] ? clients[clientId].agent : ""}
                    onChange={(event) => { 
                        if (clients) {
                            const clientId = Object.keys(clients).find(key => clients[key].agent === event.target.value);
                            setClientId(clientId);
                        }
                    }}
                    data={clients ? ["", ...Object.keys(clients).map((key) => { return clients[key].agent || "" })] : []}
                />

                {/* Owners */}
                <Space h="md" />
                <Text style={{ fontWeight: "bold" }}>Owners</Text>
                <Button onClick={() => { setOwners(["", ...owners]) }}>Add Owner</Button>
                <Space h="md" />
                {owners
                    .map((owner, index) => {
                    return (
                        <div key={`owner_${index.toString()}`}>
                            <Grid>
                                <Grid.Col span={1}>
                                    <ActionIcon onClick={() => { owners.splice(index, 1); setOwners([...owners]); }}><IconTrash /></ActionIcon>
                                </Grid.Col>

                                <Grid.Col span={11}>
                                    <TextInput placeholder="Owner" value={owner} onChange={(event) => { owners[index] = event.target.value; setOwners([...owners]); }} />
                                </Grid.Col>
                            </Grid>
                        </div>
                    );
                })}

                </>
            }

            <Space h="md" />
            <Text size="lg" style={{ fontWeight: "bold" }}>Deadlines</Text>

            <Button onClick={() => { setDeadlines([{ 
                    title: "", 
                    description: "", 
                    dueDate: null, 
                    isComplete: false
                }, ...deadlines]) }}
            >Add Deadline</Button>

            <Button ml={10} onClick={() => { setIsShowingOfficeActionModal(true); }}>Add Office Action</Button>

            <Space h="md" />
            <div ref={refDeadlineList}>
                {deadlines
                    .sort((a, b) => { 
                        const at = (a.dueDate ? a.dueDate : 0);
                        const bt = (b.dueDate ? b.dueDate : 0);
                        return at - bt;
                    })
                    .map((deadline, index) => {
                    return (
                        <div key={index}>
                            <DeadlineRow
                                deadline={deadline}
                                onDeleteClick={() => { deadlines.splice(index, 1); setDeadlines([...deadlines]); }}
                                onCompleteClick={() => { deadlines[index].isComplete = !deadline.isComplete; setDeadlines([...deadlines]); }}
                                onTitleChange={(text) => { deadlines[index].title = text; setDeadlines([...deadlines]); }}
                                onDescriptionChange={(text) => { deadlines[index].description = text; setDeadlines([...deadlines]); }}
                                onDueDateChange={(date) => { deadlines[index].dueDate = date ? date.getTime() : null; setDeadlines([...deadlines]); }}
                                onTitleBlur={() => { 
                                    const deadlines = App.Data.App.TrademarkUtils.validateDeadlines(getTrademarkFromState());
                                    const renewalDue = deadlines.find(x => x.title === "RENEWAL DUE");
                                    if (renewalDue && renewalDue.dueDate) { setExpirationDate(new Date(renewalDue.dueDate)); }
                                    setDeadlines(deadlines);
                                    forceUpdate();
                                }}
                                onDueDateBlur={() => { 
                                    const deadlines = App.Data.App.TrademarkUtils.validateDeadlines(getTrademarkFromState());
                                    const renewalDue = deadlines.find(x => x.title === "RENEWAL DUE");
                                    if (renewalDue && renewalDue.dueDate) { setExpirationDate(new Date(renewalDue.dueDate)); }
                                    setDeadlines(deadlines);
                                    forceUpdate(); 
                                }}
                            />
                        </div>
                    );
                })}
            </div>

            <div style={{ paddingTop: "50px" }} />
        </Container>
    );
}